import { applySentryToVueApp, decode } from '#libs'
import { $lastActiveElement } from '#stores'
import { logger } from '@nanostores/logger'
import { devtools } from '@nanostores/vue/devtools'
import type { AnyStore } from 'nanostores'
import { createApp, type App, type Component as VueComponent } from 'vue'
import { z } from 'zod'

export type ComponentElement<
  TPropsSchema extends z.AnyZodObject = z.AnyZodObject,
  TUtilities extends Record<string, unknown> = Record<string, unknown>
> = InstanceType<ReturnType<typeof buildComponentConstructor<TPropsSchema, TUtilities>>> & {
  props: z.infer<TPropsSchema> /* Here we assume every valid component has props after parsing (empty object allowed) */
}

type ComponentConfig<
  TPropsSchema extends z.AnyZodObject = z.AnyZodObject,
  TUtilities extends Record<string, unknown> = Record<string, unknown>
> = {
  name: string
  propsSchema?: TPropsSchema
  vueComponent?: VueComponent // Required if element 'data-is-vue' is true
  debugStores?: Record<string, AnyStore>

  // This is where you can put utilities that you want to use in the component
  // in special cases, like when you want to use a utility function in a component,
  // but not in other ones.
  utilities?: TUtilities

  // Lifecycle hooks of component HTML element itself (not Vue app)
  // read more: https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_custom_elements#custom_element_lifecycle_callbacks
  // always use onConnected instead of onConstructed following MDN recommendation
  // because onConstructed won't be run if component re-added to the DOM
  onConstructed?: (component: ComponentElement<TPropsSchema, TUtilities>) => Promise<void> | void
  onConnected?: (component: ComponentElement<TPropsSchema, TUtilities>) => Promise<void> | void
  onDisconnected?: (component: ComponentElement<TPropsSchema, TUtilities>) => Promise<void> | void
}

export const buildComponent = <TPropsSchema extends z.AnyZodObject, TUtilities extends Record<string, unknown>>(
  initialConfig: ComponentConfig<TPropsSchema, TUtilities>
) => {
  const config: typeof initialConfig = {
    propsSchema: z.object({}) as TPropsSchema,
    ...initialConfig,
    debugStores: {
      lastActiveElement: $lastActiveElement,
      ...initialConfig.debugStores
    }
  }

  if (customElements.get(config.name)) {
    // eslint-disable-next-line no-console
    console.warn(`Component '${config.name}' is already defined, Skip!`)

    return
  }

  const ComponentConstructor = buildComponentConstructor(config)

  customElements.define(config.name, ComponentConstructor)

  // eslint-disable-next-line no-console
  console.log(`Component '${config.name}' is built and registered successfully!`)

  return ComponentConstructor
}

const buildComponentConstructor = <TPropsSchema extends z.AnyZodObject, TUtilities extends Record<string, unknown>>({
  name,
  propsSchema,
  vueComponent,
  debugStores,
  utilities,
  onConstructed,
  onConnected,
  onDisconnected
}: ComponentConfig<TPropsSchema, TUtilities>) => {
  return class ComponentElementConstructor extends HTMLElement {
    public utilities: TUtilities | undefined = utilities
    public props: z.infer<TPropsSchema> | undefined = undefined // props can be undefined if schema is invalid
    public vueApp: App | undefined = undefined

    private destroyNanoLogger?: () => void

    constructor() {
      super()

      this.props = this.parseComponentProps()
      this.removeAttribute('data-props')

      if (!this.props) return

      onConstructed?.(this as ComponentElement<TPropsSchema, TUtilities>)
    }

    public get name() {
      return name
    }

    public get isVue() {
      return this.dataset.isVue === 'true'
    }

    connectedCallback() {
      if (this.isVue) {
        this.initVue()
      } else if (debugStores) {
        this.destroyNanoLogger = logger(debugStores)
      }

      if (!this.props) return

      onConnected?.(this as ComponentElement<TPropsSchema, TUtilities>)
    }

    disconnectedCallback() {
      this.vueApp?.unmount()
      this.vueApp = undefined

      this.destroyNanoLogger?.()

      if (!this.props) return

      onDisconnected?.(this as ComponentElement<TPropsSchema, TUtilities>)
    }

    protected initVue() {
      if (!vueComponent) {
        // eslint-disable-next-line no-console
        console.error('Vue component is not provided, cannot init!')

        return
      }

      if (this.vueApp) {
        // eslint-disable-next-line no-console
        console.warn('Vue app is already initialized, skip!')

        return
      }

      const app = createApp(vueComponent, this.props)

      // Register plugins
      debugStores && app.use(devtools, debugStores)

      // Apply Sentry
      applySentryToVueApp(app)

      app.mount(this)

      this.vueApp = app
    }

    private parseComponentProps() {
      const schema = propsSchema ?? z.object({})
      const liquidProps = JSON.parse(decode(this.dataset.props ?? '{}'))
      const parsedProps = schema.safeParse(liquidProps)

      if (!parsedProps.success) {
        // eslint-disable-next-line no-console
        console.error('[Component Builder] Invalid props provided for component', this, parsedProps.error.message)

        return
      }

      return parsedProps.data
    }
  }
}
